import type { LucideIcon } from 'lucide-react';
import {
    BarChart3,
    Building2,
    ClipboardList,
    Home as HomeIcon,
    Landmark,
    LayoutDashboard,
    MapPin,
    MessageSquareHeart,
    MessagesSquare,
    Utensils,
} from 'lucide-react';
import type { IndustryInfo } from '@/modules/marketing/components/industry-visuals';

/**
 * PricingCRM's own real positioning — sourced from the one page of the legacy
 * pricingcrm.com WordPress site that was actually written (its "Home" page;
 * every other WP page there was unedited theme-demo Lorem ipsum, so this is
 * genuinely the only authentic PricingCRM copy that exists to draw from).
 * Used only when isMarketingOnlyDomain() is true — aipa.pricingcrm.com keeps
 * its own AIPA content/INDUSTRIES/FEATURES entirely untouched.
 */
export const PRICINGCRM_TAGLINE = 'Insights for Better Pricing';

export const PRICINGCRM_ABOUT =
    'Unlock the power of strategic pricing with PricingCRM, the ultimate B2B solution tailored for senior living, real estate, hospitality, and corporate portfolios. Seamlessly analyze market trends, compare competitor pricing, and enhance decision-making with robust graphical reports. Empower your business to stay ahead with data-driven insights and optimized pricing strategies — transform your pricing model into a competitive advantage.';

export const PRICINGCRM_INDUSTRIES: IndustryInfo[] = [
    {
        slug: 'senior-living',
        icon: Building2,
        name: 'Senior Living',
        tagline: 'Meet the evolving needs of residents',
        description:
            'Empowers senior living facilities to stay competitive through real-time market insights, optimizing pricing strategies and service offerings against every community you compete with.',
        bullets: [
            'Live competitor rate comparisons across every community in your market',
            'Care-level and unit-type pricing kept aligned with real demand',
            'Occupancy and rent-roll data in one place, ready for your next rate review',
        ],
    },
    {
        slug: 'corporates',
        icon: Landmark,
        name: 'Corporates',
        tagline: 'Pricing alignment with market demands and business objectives',
        description:
            'Data analytics that support strategic decision-making to align your pricing with your broader business goals — not just a spreadsheet of last quarter’s rates.',
        bullets: [
            'Dashboards that turn pricing data into a decision, not just a report',
            'Market positioning tracked continuously, not just at renewal time',
            'One system your whole portfolio can price from',
        ],
    },
    {
        slug: 'real-estate',
        icon: HomeIcon,
        name: 'Real Estate',
        tagline: 'Attract buyers while maximizing profits',
        description:
            'Accurate competitor analysis and market trend forecasting, helping you set competitive prices without leaving money on the table.',
        bullets: [
            'Competitor listings tracked automatically, by location and property type',
            'CMA summaries generated in minutes, not hours',
            'Pricing recommendations grounded in real market data',
        ],
    },
    {
        slug: 'hotels-restaurants',
        icon: Utensils,
        name: 'Hotels & Restaurants',
        tagline: 'Match customer expectations to stay ahead of competitors',
        description:
            'Price comparisons and sentiment analysis to adjust menus, room rates, and services as the market — and your guests’ expectations — shift.',
        bullets: [
            'Room rates and menu pricing benchmarked against nearby competitors',
            'Guest sentiment tracked alongside pricing, not in a separate tool',
            'Promotions and pricing adjusted from the same dashboard',
        ],
    },
];

export interface PricingCrmFeature {
    slug: string;
    icon: LucideIcon;
    title: string;
    description: string;
}

/** PricingCRM's real 8 features, verbatim from "The Workflow" section of the legacy site's Home page. */
export const PRICINGCRM_FEATURES: PricingCrmFeature[] = [
    {
        slug: 'lead-generator',
        icon: MessagesSquare,
        title: 'Lead Generator',
        description:
            'Installs as a script on your website to chat with visitors and capture their details — supported with a dashboard to view traffic and manage leads.',
    },
    {
        slug: 'location-management',
        icon: MapPin,
        title: 'Location Management',
        description:
            'Add your locations and your competitors’ locations, via web search or manually. View square footage, property type, price details, and more.',
    },
    {
        slug: 'pricing-management',
        icon: BarChart3,
        title: 'Pricing Management',
        description:
            'Optimize your pricing strategies with dynamic tools that adjust to market trends and competitor insights — set the right price at the right time.',
    },
    {
        slug: 'cma-summary',
        icon: ClipboardList,
        title: 'CMA Summary',
        description:
            'Instantly create detailed CMAs by comparing your locations with competitors, gaining a clear view of market positioning in minutes.',
    },
    {
        slug: 'occupancy-management',
        icon: Building2,
        title: 'Occupancy Management',
        description:
            'Strategically manage occupancy by capturing key data on pricing, rent rolls, and customer leverage to maximize occupancy and financial outcomes.',
    },
    {
        slug: 'shop-notes',
        icon: ClipboardList,
        title: 'Shop Notes',
        description:
            'Equip your team to gather competitor information by phone or web research, adding pricing, locations, and promotions straight into your system.',
    },
    {
        slug: 'dashboard-analytics',
        icon: LayoutDashboard,
        title: 'Dashboard & Analytics',
        description:
            'Access comprehensive, real-time data through intuitive dashboards — visualize key metrics and trends to drive informed decisions.',
    },
    {
        slug: 'sentiment-analysis',
        icon: MessageSquareHeart,
        title: 'Sentiment Analysis',
        description:
            'Understand customer opinions and emotions from feedback and reviews, then use those insights to strengthen customer relationships.',
    },
];
